import { createSelector } from '@reduxjs/toolkit'
import { createSelectorCache, getOrBuildSelector } from '../../Sinks/Selector/SelectorCache.ts'
import { selectFilteredCube } from '../../Sinks/Selector/SelectFilteredCube.ts'
import { forwardCubeStatus, selectCubeContext } from '../../Sinks/Selector/CubeContext.ts'
import type { FilteredBucketValues } from '../../Sinks/Selector/CubeFiltering.ts'
import type { KronaFilterOpts, TransformResult } from '../Types.ts'
import type { State } from '../../../../../Flux/Slice/index.ts'
import type { SourceId } from '../../../../../Flux/Slice/Visualization/VisualizationSliceTypes.ts'

const selectorCache = createSelectorCache<TransformResult<string[]>>()

/**
 * The path a breakdown chart should auto-open at: the point where the (filtered) taxonomy first
 * BRANCHES. When a filter leaves a single deep lineage above the first split — e.g. every pollinator
 * OTU is Eukaryota › Arthropoda › Insecta before the orders fan out, or an unfiltered set that is all
 * Eukaryota at the root — a stacked bar rooted at the current path shows one uninformative segment.
 * This returns the longest common prefix of the filtered lineages so the sink can descend straight to
 * the spread.
 *
 * Correctness: the prefix is computed over the SAME compacted `taxa` ladder that
 * `filterCubeByPathAndGrade` prefix-matches `selectedPath` against (see CubeFiltering), so the result
 * is always a valid zoom path — no rank-fill / compacted-ladder mismatch (the documented hazard of
 * this area). Only descends when ≥2 DISTINCT lineages survive the filter (a real branch to reveal);
 * a single lineage has no spread, so it returns `selectedPath` unchanged. Idempotent: re-running at
 * the returned path yields that same path, so the sink settles after a single dispatch.
 */
export function selectTaxaAutoDescendTarget(
  state: typeof State,
  sourceId: SourceId,
  _bucketKey: string | undefined,
  opts: KronaFilterOpts,
  optsKey: string
): TransformResult<string[]> {
  const cacheId = `${sourceId}|${optsKey}`

  return getOrBuildSelector(selectorCache, cacheId, () =>
    createSelector(
      [
        (s: typeof State) => selectCubeContext(s, sourceId),
        (s: typeof State) =>
          selectFilteredCube(s, sourceId, { selectedPath: opts.selectedPath, gradeThreshold: opts.gradeThreshold })
      ],
      (ctx, filtered): TransformResult<string[]> => {
        if (ctx.status !== 'ready') return forwardCubeStatus(ctx)
        return { status: 'ready', value: firstBranchPath(filtered, opts.selectedPath) }
      }
    )
  )(state)
}

/** Longest common prefix of the distinct filtered lineages, but only when there is a real branch. */
function firstBranchPath(filtered: FilteredBucketValues[], selectedPath: string[]): string[] {
  const seen = new Set<string>()
  const lineages: string[][] = []
  for (const b of filtered) {
    for (const v of b.values) {
      // NUL can't appear in a taxon name, so this key can't collide across different ladders.
      const key = v.taxa.join('\u0000')
      if (seen.has(key)) continue
      seen.add(key)
      lineages.push(v.taxa)
    }
  }
  // <2 distinct lineages → nothing to spread; leave the view where it is.
  if (lineages.length < 2) return selectedPath

  let lcpLen = Math.min(...lineages.map((l) => l.length))
  for (let j = selectedPath.length; j < lcpLen; j++) {
    const t = lineages[0]![j]
    if (!lineages.every((l) => l[j] === t)) {
      lcpLen = j
      break
    }
  }
  return lcpLen > selectedPath.length ? lineages[0]!.slice(0, lcpLen) : selectedPath
}
